import { useState, useMemo } from 'react';
import { useDeals } from '../hooks/useDeals';
import { useTasks } from '../hooks/useTasks';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/formatters';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { deals, loading: dealsLoading } = useDeals();
  const { tasks, loading: tasksLoading } = useTasks();
  const navigate = useNavigate();

  // Time filter state
  const [timeFilter, setTimeFilter] = useState('monthly');
  const [timePeriod, setTimePeriod] = useState('current');
  const [compareMode, setCompareMode] = useState(false);

  // Calculate date ranges based on filter
  const dateRanges = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3);

    let startDate, endDate, prevStartDate, prevEndDate;

    switch (timeFilter) {
      case 'monthly':
        if (timePeriod === 'current') {
          startDate = new Date(currentYear, currentMonth, 1);
          endDate = new Date(currentYear, currentMonth + 1, 0);
          prevStartDate = new Date(currentYear, currentMonth - 1, 1);
          prevEndDate = new Date(currentYear, currentMonth, 0);
        } else {
          startDate = new Date(currentYear, currentMonth - 1, 1);
          endDate = new Date(currentYear, currentMonth, 0);
          prevStartDate = new Date(currentYear, currentMonth - 2, 1);
          prevEndDate = new Date(currentYear, currentMonth - 1, 0);
        }
        break;

      case 'quarterly':
        if (timePeriod === 'current') {
          startDate = new Date(currentYear, currentQuarter * 3, 1);
          endDate = new Date(currentYear, (currentQuarter + 1) * 3, 0);
          prevStartDate = new Date(currentYear, (currentQuarter - 1) * 3, 1);
          prevEndDate = new Date(currentYear, currentQuarter * 3, 0);
        } else {
          startDate = new Date(currentYear, (currentQuarter - 1) * 3, 1);
          endDate = new Date(currentYear, currentQuarter * 3, 0);
          prevStartDate = new Date(currentYear, (currentQuarter - 2) * 3, 1);
          prevEndDate = new Date(currentYear, (currentQuarter - 1) * 3, 0);
        }
        break;

      case 'yearly':
        if (timePeriod === 'current') {
          startDate = new Date(currentYear, 0, 1);
          endDate = new Date(currentYear, 11, 31);
          prevStartDate = new Date(currentYear - 1, 0, 1);
          prevEndDate = new Date(currentYear - 1, 11, 31);
        } else {
          startDate = new Date(currentYear - 1, 0, 1);
          endDate = new Date(currentYear - 1, 11, 31);
          prevStartDate = new Date(currentYear - 2, 0, 1);
          prevEndDate = new Date(currentYear - 2, 11, 31);
        }
        break;

      case 'ytd':
        startDate = new Date(currentYear, 0, 1);
        endDate = now;
        prevStartDate = new Date(currentYear - 1, 0, 1);
        prevEndDate = new Date(currentYear - 1, now.getMonth(), now.getDate());
        break;

      default:
        startDate = new Date(currentYear, currentMonth, 1);
        endDate = new Date(currentYear, currentMonth + 1, 0);
        prevStartDate = new Date(currentYear, currentMonth - 1, 1);
        prevEndDate = new Date(currentYear, currentMonth, 0);
    }

    return {
      current: { start: startDate, end: endDate },
      previous: { start: prevStartDate, end: prevEndDate }
    };
  }, [timeFilter, timePeriod]);

  // Filter deals by date range
  const filterDealsByDate = (dealsArray, startDate, endDate) => {
    return dealsArray.filter(deal => {
      const dealDate = deal.closedAt ? new Date(deal.closedAt.seconds * 1000) : new Date(deal.createdAt.seconds * 1000);
      return dealDate >= startDate && dealDate <= endDate;
    });
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const currentDeals = filterDealsByDate(deals, dateRanges.current.start, dateRanges.current.end);
    const previousDeals = filterDealsByDate(deals, dateRanges.previous.start, dateRanges.previous.end);

    const wonDeals = timeFilter === 'ytd'
      ? deals.filter(d => d.stage === 'Won' && d.closedAt)
      : currentDeals.filter(d => d.stage === 'Won');
    const totalRevenue = wonDeals.reduce((sum, deal) => sum + (deal.value || 0), 0);

    const prevWonDeals = previousDeals.filter(d => d.stage === 'Won');
    const prevRevenue = prevWonDeals.reduce((sum, deal) => sum + (deal.value || 0), 0);
    const revenueChange = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue * 100) : 0;

    const openDeals = deals.filter(d => d.stage !== 'Won' && d.stage !== 'Lost' && !d.trashed);
    const pipelineValue = openDeals.reduce((sum, deal) => sum + (deal.value || 0), 0);
    const closingSoonCount = openDeals.filter(d => d.expectedCloseDate && new Date(d.expectedCloseDate) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)).length;

    const activeDeals = currentDeals.filter(d => d.stage !== 'Won' && d.stage !== 'Lost' && !d.trashed);
    const activeDealCount = activeDeals.length;
    const proposalStageCount = activeDeals.filter(d => d.stage === 'Proposal').length;

    const totalDealsInPeriod = currentDeals.filter(d => !d.trashed).length;
    const wonInPeriod = currentDeals.filter(d => d.stage === 'Won').length;
    const winRate = totalDealsInPeriod > 0 ? (wonInPeriod / totalDealsInPeriod * 100) : 0;

    const prevTotalDeals = previousDeals.filter(d => !d.trashed).length;
    const prevWon = previousDeals.filter(d => d.stage === 'Won').length;
    const prevWinRate = prevTotalDeals > 0 ? (prevWon / prevTotalDeals * 100) : 0;
    const winRateChange = prevWinRate > 0 ? (winRate - prevWinRate) : 0;

    return {
      totalRevenue,
      revenueChange,
      pipelineValue,
      closingSoonCount,
      activeDealCount,
      proposalStageCount,
      winRate,
      winRateChange
    };
  }, [deals, dateRanges, timeFilter]);

  // Deals by stage
  const dealsByStage = useMemo(() => {
    const stages = ['Contacts', 'Proposal', 'Negotiation'];
    const activeDeals = deals.filter(d => !d.trashed && d.stage !== 'Won' && d.stage !== 'Lost');

    return stages.map(stage => ({
      stage,
      value: activeDeals.filter(d => d.stage === stage).reduce((sum, deal) => sum + (deal.value || 0), 0),
      count: activeDeals.filter(d => d.stage === stage).length
    }));
  }, [deals]);

  // Funnel health
  const funnelData = useMemo(() => {
    const stages = ['Contacts', 'Proposal', 'Negotiation', 'Won', 'Lost'];
    const allDeals = deals.filter(d => !d.trashed);
    const maxCount = Math.max(...stages.map(stage => allDeals.filter(d => d.stage === stage).length), 1);

    return stages.map(stage => ({
      stage,
      count: allDeals.filter(d => d.stage === stage).length,
      percentage: (allDeals.filter(d => d.stage === stage).length / maxCount) * 100
    }));
  }, [deals]);

  // Upcoming tasks
  const upcomingTasks = useMemo(() => {
    const incompleteTasks = tasks.filter(t => t.status !== 'completed');
    return incompleteTasks
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 3);
  }, [tasks]);

  // Top opportunities
  const topOpportunities = useMemo(() => {
    const openDeals = deals.filter(d => d.stage !== 'Won' && d.stage !== 'Lost' && !d.trashed);
    return openDeals
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .slice(0, 3);
  }, [deals]);

  // Local formatDate with Today/Tomorrow logic specific to Dashboard
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const taskDate = new Date(date);
    taskDate.setHours(0, 0, 0, 0);

    if (taskDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (taskDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      Contacts: 'bg-neutral-100 text-neutral-700',
      Proposal: 'bg-primary-100 text-primary-700',
      Negotiation: 'bg-warning-light text-warning-dark',
      Won: 'bg-success-light text-success-dark',
      Lost: 'bg-error-light text-error-dark'
    };
    return colors[stage] || 'bg-neutral-100 text-neutral-700';
  };

  const getStageBarColor = (stage) => {
    const colors = {
      Contacts: 'bg-neutral-400',
      Proposal: 'bg-primary',
      Negotiation: 'bg-warning',
      Won: 'bg-success',
      Lost: 'bg-error'
    };
    return colors[stage] || 'bg-neutral-400';
  };

  const getMaxValue = () => {
    const maxStageValue = Math.max(...dealsByStage.map(s => s.value), 1);
    return maxStageValue;
  };

  if (dealsLoading || tasksLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" message="Loading dashboard..." />
      </div>
    );
  }

  return (
    <>
      {/* Sticky Header */}
      <div className="sticky -top-4 md:-top-8 px-6 py-5 border-b border-neutral-200 bg-white backdrop-blur-sm z-[200] -mx-4 md:-mx-8 lg:-mx-12">
        <div className="max-w-7xl mx-auto w-full">
          <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="font-display text-3xl text-neutral-900 tracking-tight">
                Welcome back, {currentUser?.displayName || 'User'}
              </h2>
              <p className="text-neutral-500 text-sm">
                Here is what is happening with your business today.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="text-sm font-medium text-neutral-700 bg-white border border-neutral-200 px-3 py-2 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary outline-none transition-all"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="ytd">YTD</option>
              </select>

              {timeFilter !== 'ytd' && (
                <select
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value)}
                  className="text-sm font-medium text-neutral-700 bg-white border border-neutral-200 px-3 py-2 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary outline-none transition-all"
                >
                  <option value="current">Current</option>
                  <option value="previous">Previous</option>
                </select>
              )}

              <button
                onClick={() => setCompareMode(!compareMode)}
                className={`flex items-center text-sm font-medium px-3 py-2 rounded-lg transition-all ${
                  compareMode
                    ? 'bg-primary text-white shadow-sm shadow-primary/25'
                    : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <span className="material-symbols-outlined text-[18px] mr-1">compare_arrows</span>
                Compare
              </button>
            </div>
          </header>
        </div>
      </div>

      {/* Stats Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <div className="flex flex-col justify-between gap-4 rounded-xl p-5 bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <p className="text-neutral-500 text-sm font-medium">
              Total Revenue {timeFilter === 'ytd' ? 'YTD' : ''}
            </p>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${metrics.revenueChange >= 0 ? 'bg-success-light' : 'bg-error-light'}`}>
              <span className={`material-symbols-outlined text-[20px] ${metrics.revenueChange >= 0 ? 'text-success' : 'text-error'}`}>
                {metrics.revenueChange >= 0 ? 'trending_up' : 'trending_down'}
              </span>
            </div>
          </div>
          <div>
            <p className="text-neutral-900 text-3xl font-bold tracking-tight">
              {formatCurrency(metrics.totalRevenue)}
            </p>
            <p className={`text-sm font-medium mt-1 min-h-[20px] ${compareMode ? (metrics.revenueChange >= 0 ? 'text-success' : 'text-error') : 'invisible'}`}>
              {metrics.revenueChange >= 0 ? '+' : ''}{metrics.revenueChange.toFixed(1)}%
              <span className="text-neutral-400 font-normal ml-1">vs previous</span>
            </p>
          </div>
        </div>

        {/* Pipeline Value */}
        <div className="flex flex-col justify-between gap-4 rounded-xl p-5 bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <p className="text-neutral-500 text-sm font-medium">Pipeline Value</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary-100">
              <span className="material-symbols-outlined text-primary text-[20px]">monetization_on</span>
            </div>
          </div>
          <div>
            <p className="text-neutral-900 text-3xl font-bold tracking-tight">
              {formatCurrency(metrics.pipelineValue)}
            </p>
            <p className="text-neutral-500 text-sm font-medium mt-1 min-h-[20px]">
              {metrics.closingSoonCount} {metrics.closingSoonCount === 1 ? 'deal' : 'deals'} closing soon
            </p>
          </div>
        </div>

        {/* Active Deals */}
        <div className="flex flex-col justify-between gap-4 rounded-xl p-5 bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <p className="text-neutral-500 text-sm font-medium">Active Deals</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-warning-light">
              <span className="material-symbols-outlined text-warning text-[20px]">folder_open</span>
            </div>
          </div>
          <div>
            <p className="text-neutral-900 text-3xl font-bold tracking-tight">
              {metrics.activeDealCount}
            </p>
            <p className="text-neutral-500 text-sm font-medium mt-1 min-h-[20px]">
              {metrics.proposalStageCount} in Proposal stage
            </p>
          </div>
        </div>

        {/* Win Rate */}
        <div className="flex flex-col justify-between gap-4 rounded-xl p-5 bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <p className="text-neutral-500 text-sm font-medium">Win Rate</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-secondary-100">
              <span className="material-symbols-outlined text-secondary text-[20px]">trophy</span>
            </div>
          </div>
          <div>
            <p className="text-neutral-900 text-3xl font-bold tracking-tight">
              {metrics.winRate.toFixed(0)}%
            </p>
            <p className={`text-sm font-medium mt-1 min-h-[20px] ${compareMode ? (metrics.winRateChange >= 0 ? 'text-success' : 'text-error') : 'invisible'}`}>
              {metrics.winRateChange >= 0 ? '+' : ''}{metrics.winRateChange.toFixed(1)}%
              <span className="text-neutral-400 font-normal ml-1">vs previous</span>
            </p>
          </div>
        </div>
      </section>

      {/* Charts Section */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Performance */}
        <div className="lg:col-span-2 rounded-xl bg-white border border-neutral-200 shadow-sm p-6 flex flex-col">
          <div className="mb-6">
            <h3 className="text-neutral-900 text-lg font-semibold">Revenue Performance</h3>
            <p className="text-sm text-neutral-500">Deal value by stage (excluding Won/Lost)</p>
          </div>

          <div className="flex-1 flex items-end justify-around gap-6 min-h-[280px]">
            {dealsByStage.map((stageData) => {
              const maxValue = getMaxValue();
              const heightPercent = maxValue > 0 ? (stageData.value / maxValue) * 100 : 0;
              const minHeight = stageData.value > 0 ? 10 : 0;
              const finalHeight = Math.max(heightPercent, minHeight);

              return (
                <div key={stageData.stage} className="flex flex-col items-center justify-end flex-1 max-w-[120px] h-full">
                  <div className="text-xs font-bold text-neutral-900 mb-2 text-center">
                    {stageData.value > 0 ? formatCurrency(stageData.value) : '—'}
                  </div>
                  <div className="w-full relative group" style={{ height: `${finalHeight}%`, minHeight: '40px' }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-500 to-primary-300 rounded-t-lg transition-all duration-300 hover:opacity-90">
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-neutral-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        {stageData.count} {stageData.count === 1 ? 'opportunity' : 'opportunities'}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs font-semibold text-neutral-600 text-center mt-3">
                    {stageData.stage}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Funnel Health */}
        <div className="rounded-xl bg-white border border-neutral-200 shadow-sm p-6 flex flex-col">
          <div className="mb-4">
            <h3 className="text-neutral-900 text-lg font-semibold">Funnel Health</h3>
            <p className="text-sm text-neutral-500">Deals by stage</p>
          </div>
          <div className="flex-1 flex flex-col justify-center gap-5">
            {funnelData.map((stage) => (
              <div key={stage.stage} className="relative group">
                <div className="flex justify-between items-end mb-1.5">
                  <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    {stage.stage}
                  </span>
                  <span className="text-xs font-bold text-neutral-700">{stage.count}</span>
                </div>
                <div className="h-2 w-full bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getStageBarColor(stage.stage)} rounded-full transition-all duration-500`}
                    style={{ width: `${stage.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Tasks */}
        <div className="rounded-xl bg-white border border-neutral-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-neutral-100 flex justify-between items-center">
            <h3 className="text-neutral-900 text-lg font-semibold">Upcoming Tasks</h3>
            <button
              onClick={() => navigate('/tasks')}
              className="text-primary text-sm font-medium hover:text-primary-600 transition-colors"
            >
              View All
            </button>
          </div>
          <div className="p-4 flex flex-col gap-1">
            {upcomingTasks.length === 0 ? (
              <div className="text-center py-8 text-neutral-400 text-sm">
                No upcoming tasks
              </div>
            ) : (
              upcomingTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => navigate('/tasks')}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-50 transition-colors cursor-pointer"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900">{task.title}</p>
                    <p className="text-xs text-neutral-500">
                      Due {formatDate(task.dueDate)} • {task.priority}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    task.priority === 'urgent' ? 'bg-error-light text-error-dark' :
                    task.priority === 'high' ? 'bg-warning-light text-warning-dark' :
                    task.priority === 'medium' ? 'bg-primary-100 text-primary-700' :
                    'bg-success-light text-success-dark'
                  }`}>
                    {task.priority}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Top Opportunities */}
        <div className="rounded-xl bg-white border border-neutral-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-neutral-100 flex justify-between items-center">
            <h3 className="text-neutral-900 text-lg font-semibold">Top Opportunities</h3>
            <button
              onClick={() => navigate('/pipeline')}
              className="text-primary text-sm font-medium hover:text-primary-600 transition-colors"
            >
              View Pipeline
            </button>
          </div>
          <div className="overflow-x-auto">
            {topOpportunities.length === 0 ? (
              <div className="text-center py-8 text-neutral-400 text-sm">
                No open opportunities
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="py-3 px-5 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                      Stage
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {topOpportunities.map((deal) => (
                    <tr
                      key={deal.id}
                      onClick={() => navigate(`/deal-details?id=${deal.id}`)}
                      className="hover:bg-neutral-50 transition-colors cursor-pointer"
                    >
                      <td className="py-4 px-5">
                        <p className="text-sm font-semibold text-neutral-900">{deal.title}</p>
                        <p className="text-xs text-neutral-500">{deal.category || 'No category'}</p>
                      </td>
                      <td className="py-4 px-5 text-sm font-semibold text-neutral-900">
                        {formatCurrency(deal.value || 0)}
                      </td>
                      <td className="py-4 px-5">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStageColor(deal.stage)}`}>
                          {deal.stage}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      <footer className="mt-4 pb-8 flex justify-center">
        <p className="text-xs text-neutral-400">
          © {new Date().getFullYear()}{' '}
          <span className="font-display">
            <span>Work</span><span className="text-primary">stack</span>
          </span>
        </p>
      </footer>
    </>
  );
};

export default Dashboard;
